import { createAsyncThunk } from '@reduxjs/toolkit'
import { WebAuthnError } from '@simplewebauthn/browser'
import {
  AuthState,
  AuthMethodEnum,
  AuthMethodOptions,
  SigninResponse,
  AuthHandlers,
  AuthMethodPasskey,
  AuthConfig,
  PkReturnError,
} from '../types.d'
import { RegistrationResponseJSON } from '@simplewebauthn/typescript-types'
import { trackEvent } from './middleware/posthog'
import { ApiError, Navigation } from '../helpers'
import { setLoading } from './slice'
import {
  hasOptedOutOfPasskeySuggestions,
  setPasskeyOptOutCookie,
} from '../utils/cookies'

const processPasskeySuggestion = async (
  config: AuthConfig,
  handlers: AuthHandlers,
  pk: SigninResponse['suggestPasskey'],
): Promise<SigninResponse['suggestPasskey']> => {
  // Never suggest if unsupported
  if (!(await handlers.platformAuthenticatorIsAvailable())) {
    return {
      suggest: false,
      prompt: null,
      storageId: null,
    }
  }

  // Check if user has opted out using storageId (unless passkey enrollment is forced)
  if (!config.forcePkEnroll && pk.storageId) {
    try {
      const hasOptedOut = hasOptedOutOfPasskeySuggestions(pk.storageId)
      if (hasOptedOut) {
        return {
          suggest: false,
          prompt: null,
          storageId: null,
        }
      }
    } catch (error) {
      console.error('Error checking opt-out status:', error)
      // Continue with normal flow if we can't check opt-out status
    }
  }

  // Always suggest if forced
  if (config.forcePkEnroll) {
    return {
      suggest: true,
      prompt:
        pk.prompt || 'With passkeys, signing in is easier and more secure.',
      storageId: pk.storageId,
    }
  }

  // Otherwise return the original suggestion
  return pk
}

// Type helper for thunk configuration
export type AuthThunkConfig = {
  state: { auth: AuthState }
  extra: AuthHandlers & { authCounterRef: React.MutableRefObject<number> }
}

export const startSignin = createAsyncThunk<
  {
    userInfo: AuthState['signin']['userInfo']
    chosenMethod: AuthMethodOptions
    autoSelected: boolean
  },
  { recaptcha: string; email: string },
  AuthThunkConfig
>(
  'auth/startSignin',
  async ({ recaptcha, email }, { getState, extra: handlers }) => {
    const { auth } = getState()

    trackEvent('signin_start', auth, { email })

    const userInfo = await handlers.queryUser(
      auth.config.csrfToken!,
      auth.config.clubId,
      auth.config.customDomain,
      recaptcha,
      email,
    )

    if (!userInfo) {
      trackEvent('user_not_found', auth, { email })
      throw new Error('SIGNUP_PROMPT')
    }

    let chosenMethod = userInfo.authMethods[0]

    if (userInfo.authMethods.includes(AuthMethodEnum.PASSKEY)) {
      if (!handlers.browserSupportsWebAuthn()) {
        userInfo.authMethods = userInfo.authMethods.filter(
          (method) => method !== AuthMethodEnum.PASSKEY,
        )

        if (chosenMethod === AuthMethodEnum.PASSKEY) {
          chosenMethod = userInfo.authMethods[0]
        }
      } else if (!(await handlers.platformAuthenticatorIsAvailable())) {
        if (
          chosenMethod === AuthMethodEnum.PASSKEY &&
          userInfo.authMethods.length > 1
        ) {
          chosenMethod = userInfo.authMethods[1]
        }
      }
    }

    const chosenMethodOptions = chosenMethod
      ? await handlers.chooseMethod(auth.config.csrfToken!, chosenMethod)
      : { type: AuthMethodEnum.UNAVAILABLE }

    trackEvent('user_found', auth, { email, chosenMethodOptions })

    return {
      userInfo,
      chosenMethod: chosenMethodOptions,
      autoSelected: true, // Method was automatically selected
    }
  },
)

export const chooseMethod = createAsyncThunk<
  { chosenMethod: AuthMethodOptions | null; autoSelected: boolean },
  AuthMethodEnum,
  AuthThunkConfig
>(
  'auth/chooseMethod',
  async (changeToMethod, { getState, extra: handlers }) => {
    const { auth } = getState()

    if (changeToMethod === null) {
      trackEvent('method_choose', auth)
      return { chosenMethod: null, autoSelected: false }
    }

    trackEvent(`method_choose_${changeToMethod}`, auth)
    const chosenMethod = await handlers.chooseMethod(
      auth.config.csrfToken!,
      changeToMethod,
    )
    return { chosenMethod, autoSelected: false } // Explicitly chosen by user
  },
)

export const signupSubmit = createAsyncThunk<
  SigninResponse,
  { passkey?: string | null },
  AuthThunkConfig
>(
  'auth/signupSubmit',
  async ({ passkey = null }, { getState, extra: handlers, dispatch }) => {
    const { auth } = getState()

    const signUpResult = await handlers.signUpUser(
      auth.config.csrfToken!,
      auth.signup!.email,
      auth.signup!.firstName,
      auth.signup!.lastName,
    )

    trackEvent('signup_success', auth, {
      email: auth.signup!.email,
      firstName: auth.signup!.firstName,
      lastName: auth.signup!.lastName,
    })

    if (signUpResult.passkeySignals) {
      const { userId, allAcceptedCredentialIds, name, displayName } =
        signUpResult.passkeySignals
      handlers.signalAllAcceptedCredentials(userId, allAcceptedCredentialIds)
      handlers.signalCurrentUserDetails(userId, name, displayName)
    }

    signUpResult.suggestPasskey = await processPasskeySuggestion(
      auth.config,
      handlers,
      signUpResult.suggestPasskey,
    )

    // If no passkey suggestion, trigger immediate redirect
    if (!signUpResult.suggestPasskey?.suggest) {
      setTimeout(() => {
        dispatch(completeAuthFlow({ action: 'immediate' }))
      }, 0)
    }

    return signUpResult
  },
)

export const signinSubmit = createAsyncThunk<
  SigninResponse,
  { authValue: any; handleErrors?: boolean },
  AuthThunkConfig
>(
  'auth/signinSubmit',
  async (
    { authValue, handleErrors = true },
    { getState, extra: handlers, rejectWithValue, dispatch },
  ) => {
    const { auth } = getState()
    const authMethod = auth.signin!.authMethod!.type
    const termsAgreed = auth.signin?.termsAgreed || false

    try {
      trackEvent(`signin_${authMethod}_try`, auth)

      const signInResult = await handlers.signInUser(auth.config.csrfToken!, {
        authMethod: authMethod,
        credential: authValue,
        termsAgreed,
      })

      trackEvent(`signin_${authMethod}_success`, auth)

      if (signInResult.passkeySignals) {
        const { userId, allAcceptedCredentialIds, name, displayName } =
          signInResult.passkeySignals
        handlers.signalAllAcceptedCredentials(userId, allAcceptedCredentialIds)
        handlers.signalCurrentUserDetails(userId, name, displayName)
      }

      signInResult.suggestPasskey = await processPasskeySuggestion(
        auth.config,
        handlers,
        signInResult.suggestPasskey,
      )

      // If no passkey suggestion, trigger immediate redirect
      if (!signInResult.suggestPasskey?.suggest) {
        setTimeout(() => {
          dispatch(completeAuthFlow({ action: 'immediate' }))
        }, 0)
      }

      return signInResult
    } catch (error: any) {
      trackEvent(`signin_${authMethod}_error`, auth, {
        error_message: error.message,
      })
      if (!handleErrors) throw error
      return rejectWithValue(error.message)
    }
  },
)

export const enrollPasskey = createAsyncThunk<void, void, AuthThunkConfig>(
  'auth/enrollPasskey',
  async (_, { getState, extra: handlers, rejectWithValue }) => {
    const { auth } = getState()
    let attResp: RegistrationResponseJSON = null

    try {
      trackEvent('passkey_enroll_try', auth)

      const optionsJSON = await handlers.enrollPasskeyChallenge(
        auth.config.csrfToken!,
      )

      if (!optionsJSON) {
        throw new Error('No optionsJSON returned from enrollPasskeyChallenge')
      }

      attResp = await handlers.startRegistration({ optionsJSON })
      await handlers.enrollPasskey(auth.config.csrfToken!, attResp)
      trackEvent('passkey_enroll_success', auth)
    } catch (error: any) {
      if (attResp) {
        handlers.signalUnknownCredential(attResp.rawId)
      }

      trackEvent('passkey_enroll_error', auth, {
        error_message: error.message,
        terminal: error?.response?.terminal,
      })

      return rejectWithValue({
        message:
          error instanceof ApiError
            ? error.message
            : 'Something went wrong while setting up your passkey.',
        terminal: error?.response?.terminal,
      })
    }
  },
)

export const signinWithPasskey = createAsyncThunk<
  SigninResponse,
  void,
  AuthThunkConfig
>(
  'auth/signinWithPasskey',
  async (_, { getState, extra, rejectWithValue, signal, dispatch }) => {
    const { authCounterRef, ...handlers } = extra
    const { auth } = getState()

    // Increment counter to mark this as the latest auth attempt
    const startCounter = ++authCounterRef.current

    trackEvent(`signin_${AuthMethodEnum.PASSKEY}_try`, auth)

    if (!handlers.browserSupportsWebAuthn()) {
      trackEvent(`signin_${AuthMethodEnum.PASSKEY}_unsupported`, auth)
      return rejectWithValue(
        'Your browser does not support passkeys, please try another authentication method.',
      )
    }

    if (
      !auth.signin?.authMethod ||
      auth.signin.authMethod.type !== AuthMethodEnum.PASSKEY
    ) {
      trackEvent(`signin_${AuthMethodEnum.PASSKEY}_invalid_state`, auth)
      return rejectWithValue(
        'Cannot challenge passkey when auth method is not PASSKEY',
      )
    }

    try {
      const { challenge } = auth.signin.authMethod as AuthMethodPasskey
      const attResp = await handlers.startAuthentication({
        optionsJSON: challenge,
      })

      if (signal.aborted) {
        trackEvent(`signin_${AuthMethodEnum.PASSKEY}_cancelled`, auth)
        throw new Error('Request was cancelled')
      }

      // Check if we're still the current auth attempt before final signin
      if (authCounterRef.current !== startCounter) {
        console.log('Passkey signin superseded, silently stopping')
        return null
      }

      const signInResult = await handlers.signInUser(auth.config.csrfToken!, {
        authMethod: AuthMethodEnum.PASSKEY,
        credential: attResp,
      })

      // Final check before returning result
      if (authCounterRef.current !== startCounter) {
        console.log(
          'Passkey signin superseded after completion, silently stopping',
        )
        return null
      }

      trackEvent(`signin_${AuthMethodEnum.PASSKEY}_success`, auth)

      if (signInResult.passkeySignals) {
        const { userId, allAcceptedCredentialIds, name, displayName } =
          signInResult.passkeySignals
        handlers.signalAllAcceptedCredentials(userId, allAcceptedCredentialIds)
        handlers.signalCurrentUserDetails(userId, name, displayName)
      }

      signInResult.suggestPasskey = await processPasskeySuggestion(
        auth.config,
        handlers,
        signInResult.suggestPasskey,
      )

      // If no passkey suggestion, trigger immediate redirect
      if (!signInResult.suggestPasskey?.suggest) {
        setTimeout(() => {
          dispatch(completeAuthFlow({ action: 'immediate' }))
        }, 0)
      }

      return signInResult
    } catch (e: unknown) {
      const error = e instanceof Error ? e : new Error(String(e))

      let errorValue =
        'Something went wrong while signing in with your passkey.'

      if (error instanceof ApiError) {
        errorValue = error.message
      } else if (/not focused/i.test(error.message)) {
        // iOS has a habit of raising "The document is not focused." errors if we mess up the
        // passkey request timing at all. by returning SILENT_FAILURE, it just prompts the user
        // to click the 'continue' button before we prompt for the passkey again, which works,
        // instead of showing a confusing error message.
        trackEvent(`signin_${AuthMethodEnum.PASSKEY}_focused_catch`, auth, {
          error_message: error.message,
        })

        errorValue = PkReturnError.SILENT_FAILURE
      }

      if (errorValue !== PkReturnError.SILENT_FAILURE) {
        trackEvent(`signin_${AuthMethodEnum.PASSKEY}_error`, auth, {
          error_message: error.message,
        })
      }

      return rejectWithValue(errorValue)
    }
  },
)

export const signinWithAnonymousPasskey = createAsyncThunk<
  SigninResponse | null,
  void,
  AuthThunkConfig
>(
  'auth/signinWithAnonymousPasskey',
  async (_, { getState, extra, rejectWithValue, signal, dispatch }) => {
    const { authCounterRef, ...handlers } = extra
    const { auth } = getState()

    // Increment counter to mark this as the latest auth attempt
    const startCounter = ++authCounterRef.current

    if (!(await handlers.browserSupportsWebAuthnAutofill())) {
      return null
    }

    try {
      const response = await handlers.anonymousSigninPasskeyChallenge(
        auth.config.csrfToken!,
        auth.config.clubId,
        auth.config.customDomain,
      )

      if (!response) {
        return null
      }

      let attResp: any

      try {
        const optionsJSON = response.challenge
        attResp = await handlers.startAuthentication({
          optionsJSON,
          useBrowserAutofill: true,
        })

        if (signal.aborted) {
          throw new Error('Request was cancelled')
        }
      } catch (error: any) {
        console.log(
          'setup error on anonymous passkey challenge, skipping',
          error,
        )
        return null
      }

      try {
        if (!attResp) {
          return null
        }

        // Check if we're still the current auth attempt before proceeding
        if (authCounterRef.current !== startCounter) {
          console.log('Anonymous passkey signin superseded, silently stopping')
          return null
        }

        // Set loading state only after successful authentication
        dispatch(setLoading(true))

        const signInResult = await handlers.anonymousSigninPasskey(
          auth.config.csrfToken!,
          attResp,
        )

        // Final check before returning result
        if (authCounterRef.current !== startCounter) {
          console.log(
            'Anonymous passkey signin superseded after completion, silently stopping',
          )
          return null
        }

        trackEvent(`signin_anonymous_${AuthMethodEnum.PASSKEY}_success`, auth)

        if (signInResult.passkeySignals) {
          const { userId, allAcceptedCredentialIds, name, displayName } =
            signInResult.passkeySignals
          handlers.signalAllAcceptedCredentials(
            userId,
            allAcceptedCredentialIds,
          )
          handlers.signalCurrentUserDetails(userId, name, displayName)
        }

        signInResult.suggestPasskey = await processPasskeySuggestion(
          auth.config,
          handlers,
          signInResult.suggestPasskey,
        )

        // If no passkey suggestion, trigger immediate redirect
        if (!signInResult.suggestPasskey?.suggest) {
          setTimeout(() => {
            dispatch(completeAuthFlow({ action: 'immediate' }))
          }, 0)
        }

        return signInResult
      } catch (error: any) {
        // User cancellations - remain silent
        if (
          error instanceof WebAuthnError &&
          (error.code === 'ERROR_CEREMONY_ABORTED' ||
            (error.code === 'ERROR_PASSTHROUGH_SEE_CAUSE_PROPERTY' &&
              (error as any).cause?.name === 'NotAllowedError'))
        ) {
          console.log('silent error - user cancelled the passkey prompt', error)
          return null
        }

        // Terminal errors - show the error and don't retry
        if (error instanceof ApiError && error.response?.terminal) {
          trackEvent(`signin_anonymous_${AuthMethodEnum.PASSKEY}_error`, auth, {
            error_message: error.message,
            terminal: true,
          })

          return rejectWithValue({ message: error.message, terminal: true })
        }

        // API errors that should be shown to user (authentication failures, etc)
        if (error instanceof ApiError) {
          trackEvent(`signin_anonymous_${AuthMethodEnum.PASSKEY}_error`, auth, {
            error_message: error.message,
            terminal: false,
          })
          // Return the error so it shows in the UI
          return rejectWithValue(error.message)
        }

        // Only silently fail for unexpected/browser errors during setup
        console.log(
          'silent error - anonymous passkey setup failed, allowing manual login',
          error.message,
        )
        return null
      }
    } catch (error: any) {
      console.error('challengeAnonymousPasskey silent error:', error)
      return null
    }
  },
)

export const completeAuthFlow = createAsyncThunk<
  void,
  { action: 'skip' | 'created' | 'immediate' },
  AuthThunkConfig
>(
  'auth/completeAuthFlow',
  async ({ action }, { dispatch, getState, extra: handlers }) => {
    const state = getState().auth

    // Handle skip opt-out cookie
    if (
      action === 'skip' &&
      state.config.passkeyStorageId &&
      !state.config.forcePkEnroll
    ) {
      setPasskeyOptOutCookie(state.config.passkeyStorageId)
    }

    // Track event for passkey actions
    if (action !== 'immediate') {
      trackEvent(action === 'skip' ? 'passkey_skip' : 'passkey_created', state)
    } else {
      trackEvent('end', state)
    }

    // Flush PostHog analytics before navigation
    if (typeof window !== 'undefined' && window.posthog) {
      try {
        window.posthog._handle_unload()
      } catch (error) {
        console.warn('PostHog unload error:', error)
      }
    }

    // Fetch return URL from backend (more reliable than static config)
    const returnUrl = await handlers.fetchReturnUrl(state.config.csrfToken!)

    // Navigate to the URL with delay to ensure analytics are sent
    setTimeout(() => Navigation.navigate(returnUrl), 700)
  },
)
